import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import AuthMedia from "./AuthMedia";
import { ShieldAlert, Upload, Loader2, Clock, CheckCircle2, FileText, XCircle } from "lucide-react";
import { toast } from "sonner";

const QUALIFICHE = ["Erede", "Familiare", "Altro avente diritto"];

// Comunicazione della scomparsa: caricamento del certificato da parte di chi ha titolo.
const DeathProcess = ({ token, process, ownerName, onUpdated }) => {
  const fileRef = useRef(null);
  const [comune, setComune] = useState("");
  const [qualifica, setQualifica] = useState("Erede");
  const [uploaderName, setUploaderName] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const status = process?.death_status || "alive";
  const meta = process?.certificate_meta;

  const submit = async () => {
    if (!file) { toast.error("Allega il certificato di morte"); return; }
    if (!comune.trim()) { toast.error("Indica il Comune che ha rilasciato il certificato"); return; }
    if (!uploaderName.trim()) { toast.error("Indica il tuo nome e cognome"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("comune", comune.trim());
      fd.append("qualifica", qualifica);
      fd.append("uploader_name", uploaderName.trim());
      fd.append("note", note.trim());
      await api.post(`/death/certificate/${token}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificato inviato. In attesa di verifica ufficiale.");
      setFile(null); setComune(""); setUploaderName(""); setNote("");
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-border p-8" data-testid="death-process">
      {status === "verified" ? (
        <div data-testid="death-process-verified">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-[#4e8f6f]" />
            <h2 className="font-serif text-2xl">Verifica completata</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed mb-5">
            La scomparsa di {ownerName} è stata verificata ufficialmente. Tutte le volontà custodite sono ora disponibili.
          </p>
          {process.certificate && (
            <div className="bg-[#F4F1EA] rounded-xl p-4">
              <p className="text-sm font-medium text-[#2C2A29] mb-1 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" /> Certificato di morte — Comune di {process.certificate.comune}
              </p>
              <p className="text-xs text-muted-foreground mb-3">Comunicato da {process.certificate.uploaded_by} ({process.certificate.qualifica || process.certificate.uploaded_by_role})</p>
              {process.certificate.kind === "image"
                ? <AuthMedia item={process.certificate} guardianToken={token} className="w-full max-h-96 object-contain rounded-lg bg-white" />
                : <AuthMedia item={process.certificate} guardianToken={token} />}
            </div>
          )}
        </div>
      ) : status === "pending_verification" ? (
        <div data-testid="death-process-pending">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-[#c9694f]" />
            <h2 className="font-serif text-2xl">Verifica in corso</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            Il certificato di morte {meta?.comune ? `(Comune di ${meta.comune}) ` : ""}è stato ricevuto ed è ora in verifica ufficiale.
            Le volontà di {ownerName} saranno rese disponibili solo a verifica completata.
          </p>
        </div>
      ) : (
        <div data-testid="death-process-upload">
          <div className="flex items-start gap-3 bg-[#FBEDE7] rounded-xl p-4 mb-5">
            <ShieldAlert className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Comunica la scomparsa</p>
              <p className="text-sm text-muted-foreground mt-1">
                Se {ownerName} è venuto a mancare, chi ne ha titolo (erede, familiare o altro avente diritto) può comunicarlo caricando il <strong>certificato di morte</strong> rilasciato dal Comune. Sarà verificato dagli uffici e, solo dopo la verifica, tutte le volontà verranno rilasciate.
              </p>
            </div>
          </div>

          {status === "rejected" && (
            <div className="flex items-start gap-3 bg-[#FBE9E9] border border-destructive/30 rounded-xl p-4 mb-5" data-testid="death-process-rejected">
              <XCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Certificato non accettato</p>
                <p className="text-sm text-muted-foreground mt-1">{process.rejection_reason || "Il documento non è stato ritenuto valido. Carica un nuovo certificato."}</p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1.5">In quale veste comunichi la scomparsa?</label>
              <select value={qualifica} onChange={(e) => setQualifica(e.target.value)} className="w-full bg-[#F4F1EA] border border-border rounded-xl h-11 px-4 text-sm outline-none focus:border-primary/50" data-testid="certificate-qualifica-select">
                {QUALIFICHE.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Il tuo nome e cognome</label>
              <input value={uploaderName} onChange={(e) => setUploaderName(e.target.value)} placeholder="Es. Mario Rossi" className="w-full bg-[#F4F1EA] border border-border rounded-xl h-11 px-4 text-sm outline-none focus:border-primary/50" data-testid="certificate-uploader-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Comune di residenza che ha rilasciato il certificato</label>
              <input value={comune} onChange={(e) => setComune(e.target.value)} placeholder="Es. Milano" className="w-full bg-[#F4F1EA] border border-border rounded-xl h-11 px-4 text-sm outline-none focus:border-primary/50" data-testid="certificate-comune-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Nota (facoltativa)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. figlio del defunto" className="w-full bg-[#F4F1EA] border border-border rounded-xl h-11 px-4 text-sm outline-none focus:border-primary/50" data-testid="certificate-note-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1.5">Certificato di morte (immagine o PDF)</label>
              <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-sm rounded-full bg-white border border-border h-11 px-5 hover:border-primary/40 transition-colors" data-testid="certificate-file-btn">
                <Upload className="w-4 h-4" /> {file ? file.name : "Allega il documento"}
              </button>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="certificate-file-input" />
            </div>
            <button onClick={submit} disabled={uploading} className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-11 px-6 text-sm hover:bg-[#c9694f] transition-colors disabled:opacity-60" data-testid="certificate-submit-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Invia per la verifica
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeathProcess;
